import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert
} from '@mui/material';
import { ChangeTimeline } from '../../components/changes/ChangeTimeline';
import { changeDetectionService } from '../../services/changeDetectionService';
import type { ChangeFilters, ChangeType, Severity } from '../../types/changeDetection.types';

const changeTypeOptions: { value: ChangeType; label: string }[] = [
  { value: 'META', label: 'Meta Tags' },
  { value: 'CANONICAL', label: 'Canonical' },
  { value: 'INDEXABILITY', label: 'Indexability' },
  { value: 'CONTENT', label: 'Content' },
  { value: 'TEMPLATE', label: 'Template' },
  { value: 'SCHEMA', label: 'Schema' }
];

const severityOptions: { value: Severity; label: string }[] = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' }
];

export const ChangeHistoryPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const pageUrl = searchParams.get('pageUrl');
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ChangeFilters>({});

  // Use pageId if available (from path), otherwise use pageUrl (from query param)
  const lookupKey = pageId ? `pageId-${pageId}` : `pageUrl-${pageUrl}`;

  const { data: changes, isLoading, error } = useQuery({
    queryKey: ['pageChanges', lookupKey, filters],
    queryFn: () => {
      if (pageId) {
        // Use legacy endpoint with pageId
        return changeDetectionService.getPageChanges(Number(pageId), filters);
      } else if (pageUrl) {
        // Use new endpoint with pageUrl
        return changeDetectionService.getPageChangesByUrl(pageUrl, filters);
      }
      return Promise.resolve([]);
    },
    enabled: !!pageId || !!pageUrl,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });

  const handleTypeFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFilters({
      ...filters,
      types: typeof value === 'string' ? [] : (value as ChangeType[])
    });
  };

  const handleSeverityFilterChange = (event: SelectChangeEvent<Severity | ''>) => {
    const value = event.target.value as Severity | '';
    setFilters({
      ...filters,
      minSeverity: value || undefined
    });
  };

  const handleCreateExperiment = () => {
    // Navigate to experiment form with page context
    if (pageId) {
      navigate(`/experiments/new?pageId=${pageId}`);
    } else if (pageUrl) {
      navigate(`/experiments/new?pageUrl=${encodeURIComponent(pageUrl)}`);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load change history. Please try again later.
        </Alert>
      </Box>
    );
  }

  const displayUrl = pageUrl || `Page ID: ${pageId}`;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Change History
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Track detected SEO-related changes to {displayUrl} over time.
      </Typography>

      {/* Filters Panel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Change Types</InputLabel>
            <Select
              multiple
              value={filters.types || []}
              onChange={handleTypeFilterChange}
              label="Change Types"
            >
              {changeTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Min Severity</InputLabel>
            <Select
              value={filters.minSeverity || ''}
              onChange={handleSeverityFilterChange}
              label="Min Severity"
            >
              <MenuItem value="">All</MenuItem>
              {severityOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Timeline */}
      <ChangeTimeline
        changes={changes || []}
        loading={isLoading}
        onCreateExperiment={handleCreateExperiment}
      />
    </Box>
  );
};
